import React, { useMemo, useState } from 'react';
import {
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Box,
    Stack,
    Divider,
    Collapse,
    TextField,
    Autocomplete,
    Button
} from '@mui/material';
import { CloseOutlined } from '@ant-design/icons';
import { PatternFormat } from 'react-number-format';
import { MoreHoriz } from '@mui/icons-material';
import Table from '../common/Table';
import useLocalTableFilters from '../../hooks/useLocalTableFilters';
import { uniqueSorted } from '../../utils/array';
import { hasIdentityVerification } from '../common/DeleteVerificationModal';

const EMPTY_FILTERS = { name: '', phone: '', status: '' };

const MembersTable = ({
    members,
    onMemberClick,
    canManage,
    isRental,
    onMakeTenant,
    onDeleteVerification,
    showFilters = false
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedMember, setSelectedMember] = useState(null);
    const { filters, handleFilterChange, handleResetFilters, hasActiveFilters } =
        useLocalTableFilters(EMPTY_FILTERS, showFilters);

    const hasActions = Boolean(
        (canManage && isRental && onMakeTenant) || onDeleteVerification
    );

    const statusOptions = useMemo(() => uniqueSorted(members, 'status'), [members]);

    const filteredMembers = useMemo(() => {
        if (!showFilters) return members;
        const name = filters.name.trim().toLowerCase();
        const phone = filters.phone.replace(/\D/g, '');
        return (members || []).filter(member => {
            if (name) {
                const fullName = `${member.first_name || ''} ${
                    member.last_name || ''
                }`.toLowerCase();
                if (!fullName.includes(name)) return false;
            }
            if (phone) {
                const memberPhone = (member.phone?.number ?? member.phone ?? '')
                    .toString()
                    .replace(/\D/g, '');
                if (!memberPhone.includes(phone)) return false;
            }
            if (filters.status && member.status !== filters.status) return false;
            return true;
        });
    }, [members, filters, showFilters]);

    const handleMenuClick = (event, member) => {
        if (!hasActions) return;
        setAnchorEl(event.currentTarget);
        setSelectedMember(member);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedMember(null);
    };

    const handleMakeTenant = () => {
        onMakeTenant?.(selectedMember);
        handleMenuClose();
    };

    const handleDeleteVerification = () => {
        onDeleteVerification?.(selectedMember);
        handleMenuClose();
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: ({ row }) => `${row.original.first_name} ${row.original.last_name}`
            },
            {
                Header: 'Phone',
                accessor: 'phone',
                Cell: ({ row }) => (
                    <Typography
                        sx={{ fontSize: '13px' }}
                        component="span"
                        variant="body1"
                        color="text.primary"
                    >
                        {row.original?.phone?.number ? (
                            <PatternFormat
                                value={row.original.phone.number}
                                displayType={'text'}
                                format={`+${row.original.phone.code} (###)-###-####`}
                            />
                        ) : (
                            // Only render `phone` when it is a plain string. A partial object (e.g.
                            // { code, country } with no number) would otherwise reach React as a
                            // child and throw "Objects are not valid as a React child".
                            (typeof row.original?.phone === 'string' &&
                                row.original.phone) ||
                            ''
                        )}
                    </Typography>
                )
            },
            {
                Header: 'Status',
                accessor: 'status',
                className: 'cell-center',
                Cell: ({ value }) => (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '80%',
                            width: '30%',
                            backgroundColor:
                                value === 'Active'
                                    ? 'rgba(45, 143, 87, .1)'
                                    : 'rgba(255, 0, 0, .1)',
                            borderRadius: '5px'
                        }}
                    >
                        <Typography
                            sx={{ fontSize: '13px', marginX: '10px' }}
                            component="span"
                            variant="body1"
                            color={
                                value === 'Active'
                                    ? 'rgba(45, 143, 87, 1)'
                                    : 'rgba(255, 0, 0, 1)'
                            }
                        >
                            {value}
                        </Typography>
                    </div>
                )
            },
            ...(hasActions
                ? [
                      {
                          Header: 'Actions',
                          className: 'cell-right',
                          Cell: ({ row }) => (
                              <IconButton
                                  onClick={e => {
                                      e.stopPropagation();
                                      handleMenuClick(e, row.original);
                                  }}
                              >
                                  <MoreHoriz />
                              </IconButton>
                          )
                      }
                  ]
                : [])
        ],
        [hasActions]
    );

    return (
        <>
            <Collapse in={showFilters}>
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', md: 'flex-start' }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fill, minmax(200px, 1fr))',
                                gap: 1.5
                            }}
                        >
                            <TextField
                                label="Name"
                                size="small"
                                fullWidth
                                value={filters.name}
                                onChange={e => handleFilterChange('name', e.target.value)}
                            />
                            <TextField
                                label="Phone"
                                size="small"
                                fullWidth
                                value={filters.phone}
                                onChange={e =>
                                    handleFilterChange('phone', e.target.value)
                                }
                            />
                            <Autocomplete
                                options={statusOptions}
                                value={filters.status || null}
                                onChange={(_, value) =>
                                    handleFilterChange('status', value || '')
                                }
                                size="small"
                                renderInput={params => (
                                    <TextField {...params} label="Status" />
                                )}
                            />
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CloseOutlined />}
                            onClick={handleResetFilters}
                            disabled={!hasActiveFilters}
                            sx={{ flexShrink: 0 }}
                        >
                            Reset
                        </Button>
                    </Stack>
                </Box>
                <Divider />
            </Collapse>
            <Table columns={columns} data={filteredMembers} onRowClick={onMemberClick} />

            {hasActions && (
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {canManage && isRental && onMakeTenant && (
                        <MenuItem onClick={handleMakeTenant}>Make Tenant</MenuItem>
                    )}

                    {onDeleteVerification && (
                        <MenuItem
                            onClick={handleDeleteVerification}
                            disabled={!hasIdentityVerification(selectedMember)}
                        >
                            Delete Verification
                        </MenuItem>
                    )}
                </Menu>
            )}
        </>
    );
};

export default MembersTable;
